import * as Yup from 'yup';
import FormFieldRadioGroup from '../components/FormField/FormFieldRadioGroup';
import FormFieldSelect from '../components/FormField/FormFieldSelect';
import FormFieldSelectConsults from '../components/FormField/FormFieldSelectConsults';
import { evaluateContextConditions, getQuestionConditions } from './conditions';
import { questionName } from './lib';

const FIELD_CHECKBOX = 'checkbox';
const FIELD_DATETIME_LOCAL = 'datetime-local';
const FIELD_EMAIL = 'email';
const FIELD_HIDDEN = 'hidden';
const FIELD_NUMBER = 'number';
const FIELD_RADIO = 'radio';
const FIELD_TEL = 'tel';
const FIELD_TEXT = 'text';
const FIELD_URL = 'url';
const FIELD_RANGE = 'range';
const FIELD_SELECT = 'select';
const FIELD_TEXTAREA = 'textarea';
const FIELD_SELECT_CONSULT = 'select_consult';

const COMPONENT_INPUT = 'input';
const COMPONENT_TEXTAREA = 'textarea';

const FIELD_TO_COMPONENT = {
  [FIELD_CHECKBOX]: COMPONENT_INPUT,
  [FIELD_DATETIME_LOCAL]: COMPONENT_INPUT,
  [FIELD_EMAIL]: COMPONENT_INPUT,
  [FIELD_HIDDEN]: COMPONENT_INPUT,
  [FIELD_NUMBER]: COMPONENT_INPUT,
  [FIELD_RADIO]: FormFieldRadioGroup,
  [FIELD_TEL]: COMPONENT_INPUT,
  [FIELD_TEXT]: COMPONENT_INPUT,
  [FIELD_URL]: COMPONENT_INPUT,
  [FIELD_RANGE]: COMPONENT_INPUT,
  [FIELD_SELECT]: FormFieldSelect,
  [FIELD_TEXTAREA]: COMPONENT_TEXTAREA,
  [FIELD_SELECT_CONSULT]: FormFieldSelectConsults,
};

export function fieldComponent(fieldType) {
  return FIELD_TO_COMPONENT[fieldType] || COMPONENT_INPUT;
}

const DATA_STRING = 'string';
const DATA_INTEGER = 'integer';
const DATA_FLOAT = 'float';
const DATA_BOOLEAN = 'boolean';
const DATA_DATE = 'date';
const DATA_TIME = 'time';
const DATA_TIMESTAMP = 'timestamp';
const DATA_URL = 'url';
const DATA_PHONE = 'phone';
const DATA_EMAIL = 'email';
const DATA_CONSULT = 'consult';

const ERROR_MESSAGE_REQUIRED = 'Please answer this question';
const ERROR_MESSAGE_NUMBER = 'Expected answer to be a number';
const ERROR_MESSAGE_TEXT = 'Expected answer to be text';
const ERROR_MESSAGE_YES_NO = 'Expected answer to be Yes or No';
const ERROR_MESSAGE_EMAIL = 'Please enter a valid email';

const dataTypeValidation = ({ question, validation }) => {
  switch (question.dataType) {
    case DATA_STRING:
      return question.config.multiple
        ? validation.array()
        : validation.string(ERROR_MESSAGE_TEXT);
    case DATA_INTEGER:
      return validation.number(ERROR_MESSAGE_NUMBER).integer();
    case DATA_FLOAT:
      return validation.number(ERROR_MESSAGE_NUMBER);
    case DATA_BOOLEAN:
      if (question.fieldType === FIELD_CHECKBOX) {
        return validation.boolean();
      }
      return validation.string(ERROR_MESSAGE_YES_NO).matches(/Yes|No/);
    case DATA_DATE:
      return validation.date();
    case DATA_TIME:
      return validation.date();
    case DATA_TIMESTAMP:
      return validation.date();
    case DATA_URL:
      return validation.string().url();
    case DATA_PHONE:
      return validation.string();
    case DATA_EMAIL:
      return validation.string().email(ERROR_MESSAGE_EMAIL);
    case DATA_CONSULT:
      return validation.string();
    default:
      return validation;
  }
};

const requiredValidation = ({ question, validation, required, hidden }) => {
  switch (true) {
    case required && !hidden:
      return validation.required(ERROR_MESSAGE_REQUIRED);
    default:
      return validation;
  }
};

export const questionValidation = ({ question, required, hidden }) => {
  let validation = Yup;
  validation = dataTypeValidation({ question, validation });
  validation = requiredValidation({ question, validation, required, hidden });

  return validation.nullable();
};

export const prepareValidationSchema = ({ questions, required, hidden }) =>
  Yup.object().shape(
    (() => {
      const validations = {};
      Object.keys(questions).forEach((name) => {
        if (!hidden[name]) {
          validations[name] = questionValidation({
            question: questions[name],
            required: required[name],
            hidden: hidden[name],
          });
        }
      });
      return validations;
    })()
  );

export const initializeForm = ({ submission, context }) => {
  const form = {
    questions: {},
    conditions: {},
    hidden: {},
    required: {},
    initialValues: {},
  };

  submission.structure.questions.forEach((question) => {
    const name = questionName(question);
    // Transform `question_name` to `questionName`
    question.name = name;
    // Prepared Question
    form.questions[name] = question;
    const { hidden, required } = evaluateContextConditions({
      question,
      context,
    });
    // Prepare Hidden
    form.hidden[name] = hidden;
    // Prepare Required
    form.required[name] = !hidden && (question.config.required || required);
    // Prepare Question Conditions (Evaluated at Runtime)
    form.conditions[name] = getQuestionConditions(question.config.conditions);
    // Prepared Initial Value
    // 1. Pre-fill Demographic Data if we have it
    // 2. Fill existing data if we have it
    // 3. Fallback to empty string for Formik
    form.initialValues[name] =
      (context?.employee?.demographicData
        ? context.employee.demographicData[name]
        : null) ||
      submission.data[name] ||
      '';
  });

  return form;
};
